import { z } from 'zod';

export type CurrencyDefinition = {
  code: string;
  name: string;
  symbol: string;
};

// Canonical ISO-4217 catalog — the ONE source of truth for every currency
// picker, validator and label in the app. It is a superset of the three lists
// that used to be maintained by hand (the pricing tab's `CURRENCY_CODES`, the
// DMC profile's `dmcCurrencyValues` and the Excel importer's
// `SUPPORTED_CURRENCIES`), which is why BRL was selectable when importing a
// sheet but not when editing a product's pricing (T-245).
//
// Ordering is intentional and drives the pickers: EUR/USD/GBP first as the
// house currencies, then the legacy `Currencies` enum order (parity with
// travel_planner_front's `currencies`), then the Lusophone-African set the
// importer accepted. Add new codes at the end of the trailing group.
export const CURRENCY_CATALOG: ReadonlyArray<CurrencyDefinition> = [
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'USD', name: 'United States Dollar', symbol: '$' },
  { code: 'GBP', name: 'British Pound Sterling', symbol: '£' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'ILS', name: 'Israeli New Shekel', symbol: '₪' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'د.م.' },
  { code: 'AOA', name: 'Angolan Kwanza', symbol: 'Kz' },
  { code: 'MZN', name: 'Mozambican Metical', symbol: 'MT' },
  { code: 'CVE', name: 'Cape Verdean Escudo', symbol: '$' },
];

export const CURRENCY_CODES = CURRENCY_CATALOG.map(
  (c) => c.code
) as ReadonlyArray<string>;

const CURRENCY_BY_CODE = new Map(CURRENCY_CATALOG.map((c) => [c.code, c]));

export const isSupportedCurrency = (code: string): boolean =>
  CURRENCY_BY_CODE.has(code);

export const findCurrency = (
  code: string | null | undefined
): CurrencyDefinition | undefined =>
  code == null ? undefined : CURRENCY_BY_CODE.get(code);

export const currencySymbol = (code: string | null | undefined): string =>
  findCurrency(code)?.symbol ?? code ?? '';

// "R$ — Brazilian Real (BRL)" — the long form used by settings pickers.
export const formatCurrencyOption = (c: CurrencyDefinition): string =>
  `${c.symbol} — ${c.name} (${c.code})`;

// Accepts any code in the catalog. Kept as a plain string schema rather than a
// z.enum so persisted values from older/other orgs never fail to load.
export const currencyCodeSchema = z
  .string()
  .refine(isSupportedCurrency, { message: 'Unsupported currency code' });
